import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Users,
  Trophy,
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Zap,
  Medal,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Loader2,
  ChevronRight,
  Flame,
  Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function QuizLiveReports() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');
  const [leaderboard, setLeaderboard] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [classAnalytics, setClassAnalytics] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAnalytics, setStudentAnalytics] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [period]);

  useEffect(() => {
    if (selectedClass) {
      loadClassAnalytics();
    }
  }, [selectedClass]);

  const loadInitialData = async () => {
    try {
      // Load class groups
      const classRes = await api.get('/class-groups');
      setClassGroups(classRes.data || []);
      
      await loadLeaderboard();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await api.get('/quiz-live/gamification/leaderboard', {
        params: { period, limit: 20 }
      });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const loadClassAnalytics = async () => {
    try {
      const response = await api.get(`/quiz-live/analytics/class/${selectedClass}`);
      setClassAnalytics(response.data);
    } catch (error) {
      console.error('Error loading class analytics:', error);
      toast.error('Erro ao carregar análise da turma');
    }
  };

  const loadStudentAnalytics = async (studentId) => {
    setSelectedStudent(studentId);
    try {
      const response = await api.get(`/quiz-live/analytics/student/${studentId}`);
      setStudentAnalytics(response.data);
    } catch (error) {
      console.error('Error loading student analytics:', error);
      toast.error('Erro ao carregar análise do aluno');
    }
  };

  const generateRecoveryQuiz = async (studentId) => {
    try {
      const response = await api.post(`/quiz-live/recovery/generate/${studentId}`, null, {
        params: { question_count: 10 }
      });
      toast.success('Quiz de recuperação gerado!');
      navigate(`/quiz-live`);
    } catch (error) {
      console.error('Error generating recovery:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar quiz');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/quiz-live')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Relatórios e Analytics</h1>
                <p className="text-white/80 text-sm">Acompanhe o desempenho dos alunos</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="leaderboard" className="space-y-6">
          <TabsList className="bg-white/10 border-white/20">
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-purple-600">
              <Trophy className="w-4 h-4 mr-2" />
              Ranking
            </TabsTrigger>
            <TabsTrigger value="class" className="data-[state=active]:bg-purple-600">
              <Users className="w-4 h-4 mr-2" />
              Por Turma
            </TabsTrigger>
            <TabsTrigger value="bncc" className="data-[state=active]:bg-purple-600">
              <Target className="w-4 h-4 mr-2" />
              BNCC
            </TabsTrigger>
          </TabsList>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Leaderboard */}
              <Card className="lg:col-span-2 bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-400" />
                      Ranking de XP
                    </CardTitle>
                    <Select value={period} onValueChange={setPeriod}>
                      <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Hoje</SelectItem>
                        <SelectItem value="weekly">Semana</SelectItem>
                        <SelectItem value="monthly">Mês</SelectItem>
                        <SelectItem value="all">Todos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  {leaderboard.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum dado de ranking ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {leaderboard.map((player, index) => (
                        <div
                          key={player.student_id}
                          onClick={() => loadStudentAnalytics(player.student_id)}
                          className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all hover:bg-white/10 ${
                            index < 3 ? 'bg-white/10' : 'bg-white/5'
                          }`}
                        >
                          <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            index === 0 ? 'bg-yellow-500 text-white' :
                            index === 1 ? 'bg-gray-400 text-white' :
                            index === 2 ? 'bg-orange-600 text-white' :
                            'bg-white/20 text-white'
                          }`}>
                            {index + 1}
                          </span>
                          
                          <div className="flex-1">
                            <p className="text-white font-medium">{player.name}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <span>{player.level_info?.badge}</span>
                              <span>Nível {player.level}</span>
                              {player.streak_days > 0 && (
                                <span className="flex items-center gap-1 text-orange-400">
                                  <Flame className="w-3 h-3" />
                                  {player.streak_days}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-xl font-bold text-purple-400">{player.xp}</p>
                            <p className="text-xs text-gray-400">XP</p>
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Student Details */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Detalhes do Aluno</CardTitle>
                </CardHeader>
                <CardContent>
                  {!studentAnalytics ? (
                    <div className="text-center py-12 text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Selecione um aluno para ver detalhes</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Overall Stats */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-white/10 rounded-xl text-center">
                          <p className="text-2xl font-bold text-white">
                            {studentAnalytics.overall_stats.total_quizzes}
                          </p>
                          <p className="text-xs text-gray-400">Quizzes</p>
                        </div>
                        <div className="p-3 bg-white/10 rounded-xl text-center">
                          <p className="text-2xl font-bold text-green-400">
                            {studentAnalytics.overall_stats.success_rate}%
                          </p>
                          <p className="text-xs text-gray-400">Acertos</p>
                        </div>
                      </div>

                      {/* Weak Skills */}
                      {studentAnalytics.weak_skills?.length > 0 && (
                        <div>
                          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            Precisa Melhorar
                          </h4>
                          <div className="space-y-2">
                            {studentAnalytics.weak_skills.slice(0, 3).map(skill => (
                              <div key={skill.skill_code} className="p-3 bg-red-500/20 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <Badge variant="outline" className="border-red-500 text-red-400 text-xs">
                                    {skill.skill_code}
                                  </Badge>
                                  <span className="text-red-400 text-sm font-bold">
                                    {skill.success_rate}%
                                  </span>
                                </div>
                                <p className="text-xs text-gray-400 truncate">
                                  {skill.description}
                                </p>
                              </div>
                            ))}
                          </div>
                          
                          <Button
                            onClick={() => generateRecoveryQuiz(selectedStudent)}
                            className="w-full mt-3 bg-amber-600 hover:bg-amber-700"
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            Gerar Quiz de Recuperação
                          </Button>
                        </div>
                      )}

                      {/* Strong Skills */}
                      {studentAnalytics.strong_skills?.length > 0 && (
                        <div>
                          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                            <Star className="w-4 h-4 text-green-400" />
                            Pontos Fortes
                          </h4>
                          <div className="space-y-2">
                            {studentAnalytics.strong_skills.slice(0, 3).map(skill => (
                              <div key={skill.skill_code} className="p-3 bg-green-500/20 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <Badge variant="outline" className="border-green-500 text-green-400 text-xs">
                                    {skill.skill_code}
                                  </Badge>
                                  <span className="text-green-400 text-sm font-bold">
                                    {skill.success_rate}%
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Class Analytics Tab */}
          <TabsContent value="class">
            <div className="space-y-6">
              {/* Class Selector */}
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Label className="text-gray-300">Selecione a turma:</Label>
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger className="w-64 bg-white/10 border-white/20 text-white">
                        <SelectValue placeholder="Escolha uma turma" />
                      </SelectTrigger>
                      <SelectContent>
                        {classGroups.map(cg => (
                          <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {classAnalytics && (
                <>
                  {/* Class Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-6 text-center">
                        <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">
                          {classAnalytics.participating_students}/{classAnalytics.total_students}
                        </p>
                        <p className="text-sm text-gray-400">Participação</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-6 text-center">
                        <Zap className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">
                          {classAnalytics.class_stats.avg_xp}
                        </p>
                        <p className="text-sm text-gray-400">XP Médio</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-6 text-center">
                        <Target className="w-8 h-8 text-green-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">
                          {classAnalytics.class_stats.success_rate}%
                        </p>
                        <p className="text-sm text-gray-400">Taxa de Acerto</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-white/5 border-white/10">
                      <CardContent className="p-6 text-center">
                        <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">
                          {classAnalytics.alerts?.length || 0}
                        </p>
                        <p className="text-sm text-gray-400">Alertas BNCC</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Student Rankings */}
                    <Card className="bg-white/5 border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white">Ranking da Turma</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                          {classAnalytics.student_rankings?.map((student, index) => (
                            <div
                              key={student.student_id}
                              onClick={() => loadStudentAnalytics(student.student_id)}
                              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer"
                            >
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                index < 3 ? 'bg-purple-600 text-white' : 'bg-white/20 text-white'
                              }`}>
                                {index + 1}
                              </span>
                              <div className="flex-1">
                                <p className="text-white text-sm">{student.name}</p>
                                <p className="text-xs text-gray-400">
                                  {student.quizzes} quizzes • {student.success_rate}% acerto
                                </p>
                              </div>
                              <span className="text-purple-400 font-bold">{student.xp} XP</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* BNCC Alerts */}
                    <Card className="bg-white/5 border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                          Habilidades com Baixo Desempenho
                        </CardTitle>
                        <CardDescription className="text-gray-400">
                          Taxa de acerto abaixo de 60%
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {classAnalytics.alerts?.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Nenhum alerta no momento</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {classAnalytics.alerts?.map(alert => (
                              <div
                                key={alert.skill}
                                className="p-4 bg-amber-500/20 rounded-xl border border-amber-500/30"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <Badge className="bg-amber-600">{alert.skill}</Badge>
                                  <span className="text-amber-400 font-bold">{alert.rate}%</span>
                                </div>
                                <Progress value={alert.rate} className="h-2" />
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* BNCC Heatmap Tab */}
          <TabsContent value="bncc">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-400" />
                  Mapa de Calor BNCC
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Selecione uma turma para ver o desempenho por habilidade
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!selectedClass ? (
                  <div className="text-center py-12 text-gray-400">
                    <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Selecione uma turma na aba "Por Turma"</p>
                  </div>
                ) : classAnalytics?.bncc_heatmap?.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum dado de habilidades ainda</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {classAnalytics?.bncc_heatmap?.map(skill => {
                      const rate = skill.success_rate;
                      const bgColor = rate >= 80 ? 'bg-green-500' :
                                     rate >= 60 ? 'bg-yellow-500' :
                                     rate >= 40 ? 'bg-orange-500' : 'bg-red-500';
                      
                      return (
                        <div
                          key={skill.skill_code}
                          className={`p-4 rounded-xl ${bgColor}/20 border border-${bgColor.replace('bg-', '')}/30 text-center`}
                        >
                          <p className="text-white font-mono text-sm font-bold mb-1">
                            {skill.skill_code}
                          </p>
                          <p className={`text-2xl font-bold ${bgColor.replace('bg-', 'text-')}`}>
                            {rate}%
                          </p>
                          <p className="text-xs text-gray-400">
                            {skill.correct}/{skill.total}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
